import { useState, useEffect, useCallback } from "react";
import {
  Button, Space, Modal, Form, Select, DatePicker, InputNumber, Input,
  Row, Col, Card, List, Tag, message,
} from "antd";
import { PlusOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import DataTable from "../../components/common/DataTable";
import KPICard, { KPIGrid } from "../../components/common/KPICard";
import ExportButton from "../../components/common/ExportButton";
import BulkUploadModal from "../../components/common/BulkUploadModal";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import { PartnerLabel } from "../../components/common/DeletedTag";
import usePermission from "../../hooks/usePermission";
import useSettingsDropdown from "../../hooks/useSettingsDropdown";
import useRiderOptions from "../../hooks/useRiderOptions";
import { formatOmr } from "./constants";
import {
  listIncentives, getIncentiveKpis, createIncentive, updateIncentive,
  deleteIncentive, downloadIncentiveTemplate, uploadIncentives, exportIncentives,
} from "../../services/accounts.service";

const { TextArea } = Input;

export default function IncentivesTab() {
  const { canEdit, canDelete } = usePermission("accounts");
  const { options: incentiveTypeOptions } = useSettingsDropdown("incentive-billers");
  const { options: projectOptions } = useSettingsDropdown("projects");
  const { options: riderOptions, loading: ridersLoading, onSearch: onRiderSearch } =
    useRiderOptions();

  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [incentiveType, setIncentiveType] = useState();
  const [period, setPeriod] = useState(null);

  const [modal, setModal] = useState({ open: false, record: null });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadPeriod, setUploadPeriod] = useState(dayjs());
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      incentiveType: incentiveType || undefined,
      month: period ? period.month() + 1 : undefined,
      year: period ? period.year() : undefined,
      ...extra,
    }),
    [search, incentiveType, period]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, kpiResponse] = await Promise.all([
        listIncentives(params({ page, per_page: pageSize })),
        getIncentiveKpis(params()),
      ]);
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
      setKpis(kpiResponse?.data || {});
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load incentives");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, incentiveType, period]);

  const openModal = (record = null) => {
    setModal({ open: true, record });
    if (record) {
      form.setFieldsValue({
        rider: record.rider?._id,
        incentiveType: record.incentiveType?._id,
        amount: record.amount,
        period: dayjs(`${record.year}-${String(record.month).padStart(2, "0")}-01`),
        project: record.project?._id,
        reason: record.reason,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ period: dayjs() });
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const { period: p, ...rest } = values;
      const payload = { ...rest, month: p.month() + 1, year: p.year() };
      if (modal.record) {
        await updateIncentive(modal.record._id, payload);
        message.success("Incentive updated");
      } else {
        await createIncentive(payload);
        message.success("Incentive added");
      }
      setModal({ open: false, record: null });
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not save incentive");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    ConfirmDeleteModal({
      title: "Delete Incentive",
      content: `Delete the ${record.monthLabel} incentive for ${record.partnerName}?`,
      onConfirm: async () => {
        try {
          await deleteIncentive(record._id);
          message.success("Incentive deleted");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not delete incentive");
        }
      },
    });
  };

  const columns = [
    {
      title: "Partner",
      dataIndex: "partnerName",
      render: (_, r) => (
        <PartnerLabel name={`${r.partnerId} — ${r.partnerName}`} deleted={r.riderDeleted} />
      ),
    },
    { title: "Month", dataIndex: "monthLabel" },
    { title: "Incentive Type", dataIndex: "incentiveTypeName" },
    { title: "Project", dataIndex: "projectName" },
    { title: "Amount (OMR)", dataIndex: "amount", align: "right", render: formatOmr },
    { title: "Reason", dataIndex: "reason", ellipsis: true },
  ];

  return (
    <div>
      <KPIGrid minWidth={170}>
        <KPICard label="Incentive Records" value={kpis.all} />
        <KPICard label="Partners" value={kpis.partners} />
        <KPICard label="Total Incentives" value={kpis.totalAmount} unit="OMR" tone="dark" />
      </KPIGrid>

      {/* BRD §5.8 asks for a top-earners list and a breakdown by type
          alongside the totals. */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card size="small" title="Top Earners">
            {(kpis.topEarners || []).length ? (
              <List
                size="small"
                dataSource={kpis.topEarners}
                renderItem={(item) => (
                  <List.Item>
                    <span>
                      {item.riderId ? `${item.riderId} — ` : ""}
                      {item.fullName || "Unknown partner"}
                    </span>
                    <b>{formatOmr(item.amount)} OMR</b>
                  </List.Item>
                )}
              />
            ) : (
              <span style={{ color: "#94a3b8" }}>No incentives in this period</span>
            )}
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card size="small" title="Breakdown by Type">
            {(kpis.byType || []).length ? (
              <List
                size="small"
                dataSource={kpis.byType}
                renderItem={(item) => (
                  <List.Item>
                    <span>
                      {item.name} <Tag>{item.count}</Tag>
                    </span>
                    <b>{formatOmr(item.amount)} OMR</b>
                  </List.Item>
                )}
              />
            ) : (
              <span style={{ color: "#94a3b8" }}>No incentives in this period</span>
            )}
          </Card>
        </Col>
      </Row>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search partner"
          allowClear
          style={{ width: 240 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="Incentive Type"
          allowClear
          style={{ width: 190 }}
          options={incentiveTypeOptions}
          value={incentiveType}
          onChange={setIncentiveType}
        />
        <DatePicker picker="month" format="MM/YYYY" onChange={setPeriod} placeholder="Month" />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton moduleName="Incentives" onExport={() => exportIncentives(params())} />
        {canEdit && (
          <>
            <Button icon={<UploadOutlined />} onClick={() => setUploadOpen(true)}>
              Upload
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
              Add Incentive
            </Button>
          </>
        )}
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
        actions={{
          onEdit: canEdit ? openModal : undefined,
          onDelete: canDelete ? handleDelete : undefined,
          canEdit,
          canDelete,
        }}
      />

      <Modal
        open={modal.open}
        title={modal.record ? "Edit Incentive" : "Add Incentive"}
        onCancel={() => setModal({ open: false, record: null })}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Save"
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="rider" label="Partner" rules={[{ required: true }]}>
            <Select
              showSearch
              filterOption={false}
              onSearch={onRiderSearch}
              loading={ridersLoading}
              options={riderOptions}
              placeholder="Search by partner ID or name"
            />
          </Form.Item>
          <Form.Item name="period" label="Incentive Month" rules={[{ required: true }]}>
            <DatePicker picker="month" format="MM/YYYY" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="incentiveType" label="Incentive Type">
            <Select options={incentiveTypeOptions} allowClear />
          </Form.Item>
          <Form.Item name="amount" label="Amount (OMR)" rules={[{ required: true }]}>
            <InputNumber style={{ width: "100%" }} precision={3} />
          </Form.Item>
          <Form.Item name="project" label="Project">
            <Select options={projectOptions} allowClear />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <BulkUploadModal
        open={uploadOpen}
        title="Upload Incentives"
        templateName="EZYGO_Incentives_Upload_Template"
        hint="CSV with Partner Id, Incentive Type, Amount and Reason. The month comes from the selector below, not the file."
        extra={
          <div>
            <div style={{ marginBottom: 4, fontSize: 12, color: "#6b7280" }}>
              Incentive month for every row in this file
            </div>
            <DatePicker
              picker="month"
              format="MM/YYYY"
              value={uploadPeriod}
              onChange={(value) => setUploadPeriod(value || dayjs())}
              allowClear={false}
              style={{ width: "100%" }}
            />
          </div>
        }
        onDownloadTemplate={downloadIncentiveTemplate}
        onUpload={(file) =>
          uploadIncentives(file, {
            month: uploadPeriod.month() + 1,
            year: uploadPeriod.year(),
          })
        }
        onClose={() => setUploadOpen(false)}
        onSuccess={fetchAll}
      />
    </div>
  );
}
